import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ThemeProvider, Typography, Checkbox,
} from '@material-ui/core';
import { usePostHog } from 'posthog-js/react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { isValidPhoneNumber } from 'react-phone-number-input';

import { ContentContainer } from './AccessCodeEntry.styles';
import {
  FlexContainer,
  HeaderBox,
  Title,
  RequiredConsentLabel,
  OptionalConsentLabel,
  // TODO: Uncomment when social auth is implemented
  // SocialButtonIconContainer,
  // DividerContainer,
  // DividerText,
  // getSocialButtonStyles,
  continueButtonStyles,
} from './CreateAccount.styles';

// TODO: Uncomment when social auth is implemented
// import GoogleIcon from '@/assets/Google.svg';
// import AppleIcon from '@/assets/Apple.svg';
// import EmailIcon from '@/assets/Email.svg';
import sparkTheme from '@/themes/sparkTheme';
import Button from '@/components/Button';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import { useConsent } from '@/hooks/useConsent';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { Header } from '@/pages/Onboarding/components/Header/Header';
import { Product } from '@/utils/productHelpers';
import { getProgram } from '@/utils/posthog';
import { PhoneNumberInput } from '@/pages/Onboarding/components/PhoneNumberInput/PhoneNumberInput';
import useBlockBackNavigation from '@/hooks/useBlockBackNavigation';

const schema = z.object({
  phoneNumber: z.string().refine(
    (v) => isValidPhoneNumber(v || ''),
    { message: 'coverage.sleepio.patient.please_enter_valid_phone_number' },
  ),
  agreeToReceiveTextMessages: z.boolean().refine((v) => v === true),
  marketingConsent: z.boolean().optional(),
});

type CreateAccountForm = z.infer<typeof schema>;

const PHONE_ERROR_MESSAGE = 'coverage.sleepio.patient.please_enter_valid_phone_number';

// TODO: Uncomment when social auth is implemented
// interface SocialButtonProps {
//   icon: React.ReactElement;
//   labelKey: string;
//   testId: string;
//   onClick: () => void;
//   marginBottom?: number;
// }
//
// const SocialButton: React.FC<SocialButtonProps> = ({
//   icon, labelKey, testId, onClick, marginBottom = 8,
// }) => {
//   const { t } = useTranslation();
//   const theme = useTheme();
//   return (
//     <Button variant="outlined" data-testid={testId} type="button" fullWidth rounded
//       style={{ ...getSocialButtonStyles(theme), marginBottom }} onClick={onClick}>
//       <SocialButtonIconContainer>{icon}</SocialButtonIconContainer>
//       <span>{t(labelKey)}</span>
//     </Button>
//   );
// };

const CreateAccountInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();
  useBlockBackNavigation();
  const history = useHistory();
  const { t } = useTranslation();
  const posthog = usePostHog();
  const { product } = useParams<{ product: Product }>();
  const program = getProgram(product);
  const methods = useForm<CreateAccountForm>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      phoneNumber: '',
      agreeToReceiveTextMessages: false,
      marketingConsent: false,
    },
  });
  const { handleSubmit, control, formState } = methods;

  useEffect(() => {
    posthog?.capture('SparkCreateAccountScreenViewed', { program });
  }, [posthog, program]);

  const onSubmit = (data: CreateAccountForm) => {
    posthog?.capture('SparkCreateAccountContinueClicked', { program });
    history.push(`/activate/${product}/sms/verify`, {
      phoneNumber: data.phoneNumber,
      agreeToReceiveTextMessages: data.agreeToReceiveTextMessages,
      marketingConsent: data.marketingConsent,
    });
  };

  // TODO: Uncomment when social auth is implemented
  // const handleSocialButtonClick = (provider: string) => {
  //   posthog?.capture(`SparkContinueWith${provider}Clicked`, { program });
  // };

  return (
    <ContentContainer>
      <Header />
      <FlexContainer maxWidth="xs">
        <HeaderBox>
          <Title variant="h2" color="textPrimary">
            {t('coverage.pre_onboarding.create_account.title')}
          </Title>
          <Typography color="textPrimary">
            {t('coverage.pre_onboarding.create_account.sub_title')}
          </Typography>
        </HeaderBox>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name="phoneNumber"
              control={control}
              rules={{
                required: t(PHONE_ERROR_MESSAGE),
                validate: (v) => isValidPhoneNumber(v || '') || t(PHONE_ERROR_MESSAGE),
              }}
              render={({ field, fieldState }) => (
                <PhoneNumberInput
                  id="phoneNumber"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={t('coverage.sleepio.patient.phone_label')}
                  error={!!fieldState.error}
                  helperText={fieldState.error ? t(PHONE_ERROR_MESSAGE) : undefined}
                  required
                />
              )}
            />

            <Controller
              name="agreeToReceiveTextMessages"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <RequiredConsentLabel
                  label={(
                    <Typography>
                      {t('coverage.pre_onboarding.create_account.agree_to_receive_text_messages_spark')}
                    </Typography>
                  )}
                  control={(
                    <Checkbox
                      {...field}
                      checked={Boolean(field.value)}
                      data-testid="consent-required-checkbox"
                    />
                  )}
                />
              )}
            />

            <Controller
              name="marketingConsent"
              control={control}
              render={({ field }) => (
                <OptionalConsentLabel
                  label={(
                    <Typography>
                      {t('coverage.pre_onboarding.create_account.marketing_consent')}
                    </Typography>
                  )}
                  control={(
                    <Checkbox
                      {...field}
                      checked={Boolean(field.value)}
                      data-testid="consent-optional-checkbox"
                    />
                  )}
                />
              )}
            />

            {/* TODO: Implement Supabase account creation and SMS sending */}
            <Button
              variant="contained"
              data-testid="continue-button"
              type="submit"
              fullWidth
              color="primary"
              disabled={!formState.isValid}
              rounded
              style={continueButtonStyles}
            >
              {t('coverage.pre_onboarding.create_account.continue')}
            </Button>

            {/* TODO: Social auth buttons will be implemented */}
            {/* <DividerContainer>
              <Divider />
              <DividerText>
                {t('coverage.pre_onboarding.create_account.or')}
              </DividerText>
            </DividerContainer>

            <SocialButton
              icon={<img src={GoogleIcon} alt="Google icon" width={20} height={20} />}
              labelKey="coverage.pre_onboarding.create_account.continue_google"
              testId="continue-google-button"
              onClick={() => handleSocialButtonClick('Google')}
            />

            <SocialButton
              icon={<img src={AppleIcon} alt="Apple icon" width={20} height={20} />}
              labelKey="coverage.pre_onboarding.create_account.continue_apple"
              testId="continue-apple-button"
              onClick={() => handleSocialButtonClick('Apple')}
            />

            <SocialButton
              icon={<img src={EmailIcon} alt="Email icon" width={20} height={20} />}
              labelKey="coverage.pre_onboarding.create_account.continue_email"
              testId="continue-email-button"
              onClick={() => handleSocialButtonClick('Email')}
              marginBottom={0}
            /> */}
          </form>
        </FormProvider>

        <ConsentWidget />
      </FlexContainer>
      <Footer />
    </ContentContainer>
  );
};

const CreateAccount: React.FC = () => (
  <ThemeProvider theme={sparkTheme}>
    <CreateAccountInner />
  </ThemeProvider>
);

export default CreateAccount;
